import {
  ChangeDetectorRef, Component, ElementRef, EventEmitter, Input, OnDestroy, OnInit, Output, ViewChild,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  BotChip, BotContext, BotHandoff, BotIdentity, BotMode, BotReply, SupportBotService,
} from '@app-shared/services/support-bot.service';

interface BotRow {
  id: number;
  sender: 'incoming' | 'outgoing' | 'system';
  text: string;
  time: string;
}

@Component({
  selector: 'app-support-bot',
  templateUrl: './support-bot.component.html',
  styleUrls: ['./support-bot.component.scss'],
  standalone: false,
})
export class SupportBotComponent implements OnInit, OnDestroy {
  @Input() context!: BotContext;
  @Output() handoff = new EventEmitter<BotHandoff>();
  @Output() identity = new EventEmitter<BotIdentity>();
  @Output() ticketCreated = new EventEmitter<{ id: number }>();
  @ViewChild('scrollBody') scrollBody?: ElementRef;

  rows: BotRow[] = [];
  chips: BotChip[] = [];
  draft = '';
  isTyping = false;
  isBusy = false;
  mode: BotMode = 'bot';
  highlightEscalation = false;

  private sessionId = '';
  private previousChips: BotChip[] = [];
  private seq = 0;
  private destroyed = false;
  private subs = new Subscription();

  constructor(private bot: SupportBotService, private cdr: ChangeDetectorRef) {}

  ngOnInit(): void {
    this.startSession();
  }

  ngOnDestroy(): void {
    this.destroyed = true;
    if (this.sessionId) {
      this.bot.end(this.sessionId).subscribe({ error: () => {} });
    }
    this.subs.unsubscribe();
  }

  // ---- template helpers ----
  get showUniversalChips(): boolean {
    return this.mode !== 'human' && this.mode !== 'ended';
  }

  get showInput(): boolean {
    return this.mode !== 'human' && this.mode !== 'ended';
  }

  get placeholder(): string {
    switch (this.mode) {
      case 'awaiting_email': return 'Type your email address';
      case 'awaiting_phone': return 'Type your phone number';
      case 'awaiting_note': return 'Type a note, or tap Skip';
      default: return 'Type your question';
    }
  }

  trackByRow(_: number, row: BotRow): number {
    return row.id;
  }

  // ---- actions ----
  askTicket(): void {
    this.sendChip({ label: 'Create ticket', action: 'ticket' });
  }

  askHuman(): void {
    this.sendChip({ label: 'Talk to human', action: 'human' });
  }

  askBack(): void { this.sendChip({ label: 'Back', action: 'back' }); }
  askMenu(): void { this.sendChip({ label: 'Menu', action: 'menu' }); }

  sendChip(chip: BotChip): void {
    if (this.isBusy || !this.sessionId) return;
    this.addRow('outgoing', chip.label);
    this.beginRequest();
    this.subs.add(this.bot.sendChip(this.sessionId, chip).subscribe({
      next: (reply) => this.render(reply),
      error: (err) => this.handleError(err),
    }));
  }

  sendText(event?: Event): void {
    if (event) event.preventDefault();
    const text = (this.draft || '').trim();
    if (!text || this.isBusy || !this.sessionId) return;
    this.draft = '';
    this.addRow('outgoing', text);
    this.beginRequest();
    this.subs.add(this.bot.sendText(this.sessionId, text).subscribe({
      next: (reply) => this.render(reply),
      error: (err) => this.handleError(err),
    }));
  }

  // ---- internals ----
  private startSession(): void {
    this.isBusy = true;
    this.chips = [];
    this.subs.add(this.bot.start(this.context).subscribe({
      next: (reply) => this.render(reply),
      error: () => {
        this.isBusy = false;
        this.addRow('system', "Couldn't start the assistant. Close and reopen this chat to try again.");
      },
    }));
  }

  private beginRequest(): void {
    this.previousChips = this.chips;
    this.chips = [];
    this.isBusy = true;
    this.cdr.detectChanges();
  }

  private async render(reply: BotReply): Promise<void> {
    this.sessionId = reply.session_id;
    this.mode = reply.mode;
    this.highlightEscalation = !!reply.suggest_escalation;

    for (const message of reply.messages) {
      this.isTyping = true;
      this.cdr.detectChanges();
      this.scrollToBottom();
      await this.delay(Math.min(900, 300 + message.text.length * 5));
      if (this.destroyed) return;
      this.isTyping = false;
      this.addRow('incoming', message.text);
    }

    this.chips = reply.chips || [];
    this.isBusy = false;

    if (reply.user && (reply.user.email || reply.user.phone || reply.user.name)) {
      this.identity.emit(reply.user);
    }
    if (reply.ticket) {
      this.ticketCreated.emit({ id: reply.ticket.id });
    }
    if (reply.handoff) {
      this.addRow('system', 'Connecting you to live support...');
      await this.delay(900);
      if (!this.destroyed) this.handoff.emit(reply.handoff);
    }
    this.cdr.detectChanges();
    this.scrollToBottom();
  }

  private handleError(err: any): void {
    this.isBusy = false;
    this.isTyping = false;
    const expired = err && (err.status === 410 || (err.error && err.error.code === 'SESSION_EXPIRED'));
    if (expired) {
      this.sessionId = '';
      this.addRow('system', 'This chat timed out. Starting a new one.');
      this.startSession();
      return;
    }
    this.chips = this.previousChips;
    this.addRow('system', "That didn't go through. Please try again.");
  }

  private addRow(sender: BotRow['sender'], text: string): void {
    const time = new Date().toLocaleTimeString([], { hour: 'numeric', minute: '2-digit' });
    this.rows.push({ id: ++this.seq, sender, text, time });
    this.cdr.detectChanges();
    this.scrollToBottom();
  }

  private delay(ms: number): Promise<void> {
    return new Promise((resolve) => setTimeout(resolve, ms));
  }

  private scrollToBottom(): void {
    setTimeout(() => {
      const el = this.scrollBody && this.scrollBody.nativeElement;
      if (el) el.scrollTop = el.scrollHeight;
    }, 30);
  }
}